import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getChatById, fetchFolders, updateChatFolder } from "./api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faRotate,
  faBug,
  faFeather,
  faLightbulb,
  faHandsHelping,
  faHeart,
  faChevronDown,
  faChevronUp,
  faBook,
} from "@fortawesome/free-solid-svg-icons";
import TextareaAutosize from "react-textarea-autosize";
import MessageBuilderModal from "./MessageBuilderModal";

const API_URL = process.env.REACT_APP_API_URL;
const fetchConfig = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};
const STATIC_CATEGORIES = [
  "bug",
  "feature",
  "improvement",
  "assistance",
  "appreciation",
];

const ChatView = () => {
  const [searchParams] = useSearchParams();
  const [chat, setChat] = useState(null);
  const [folders, setFolders] = useState([]);
  const [reply, setReply] = useState("");
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const { id } = useParams();
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const fetchChat = async () => {
      const data = await getChatById(id);
      const folderParam = searchParams.get("folder");
      if (folderParam) data.folder = folderParam;
      setChat(data);
    };
    fetchChat();
    // load folders
    (async () => {
      const res = await fetchFolders();
      setFolders(res.folders);
    })();
    // load custom message categories
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/support/custom/messages`,
          fetchConfig
        );
        const data = await res.json();
        setCustomCategories(Object.keys(data.messages));
      } catch (e) {
        console.error("Failed to load custom categories", e);
      }
    })();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [chat?.messages]);

  useEffect(() => {
    if (isMobile) {
      const handleResize = () => {
        // Detect keyboard visibility by comparing window height changes
        const isKeyboardVisible = window.innerHeight < window.outerHeight * 0.8;
        setKeyboardVisible(isKeyboardVisible);
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const openBuilder = () => setIsBuilderOpen(true);
  const handleInsert = (msg) => {
    setReply(msg);
    setIsBuilderOpen(false);
  };

  const sendMessage = async () => {
    if (!reply.trim()) return;

    const newMsg = {
      sender: "Support",
      content: reply,
    };

    // Optimistically update UI
    setChat((prev) => ({
      ...prev,
      messages: [...(prev.messages || []), newMsg],
    }));

    await fetch(`${API_URL}/support/add_message/${chat.type}s/${chat._id}`, {
      ...fetchConfig,
      method: "POST",
      body: JSON.stringify(newMsg),
    });

    // Send email/push notification
    const notificationReq = {
      recipient: chat.user.email,
      subject: "Support Studizy",
      body: reply,
      push_token: chat.push_token,
    };

    await fetch(`${API_URL}/support/send_email`, {
      ...fetchConfig,
      method: "POST",
      body: JSON.stringify(notificationReq),
    });

    // Automatically update status to Handled
    await fetch(
      `${API_URL}/support/update_status/${chat.type}s/${chat._id}/Handled`,
      { ...fetchConfig, method: "GET" }
    );
    setChat((prev) => ({ ...prev, status: "Handled" }));

    setReply("");
  };

  const handleReload = async () => {
    const data = await getChatById(id);
    setChat(data);
  };

  const handleStatusChange = async (status) => {
    const res = await fetch(
      `${API_URL}/support/update_status/${chat.type}s/${chat._id}/${status}`,
      { ...fetchConfig, method: "GET" }
    );
    const data = await res.json();
    console.log("Status Change Response:", data);
    setChat({ ...chat, status: status });
  };

  const handleCategoryChange = async (category) => {
    const res = await fetch(
      `${API_URL}/support/update_category/${chat.type}s/${chat._id}/${category}`,
      { ...fetchConfig, method: "GET" }
    );
    const data = await res.json();
    setChat({ ...chat, category: category });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "bug":
        return faBug;
      case "feature":
        return faFeather;
      case "improvement":
        return faLightbulb;
      case "assistance":
        return faHandsHelping;
      case "appreciation":
        return faHeart;
      default:
        return null;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "bug":
      case "feature":
      case "improvement":
        return "#3273dc"; // technical categories
      case "assistance":
        return "#ffdd57";
      case "appreciation":
        return "#00d1b2";
      default:
        return "#3273dc";
    }
  };

  // merge static and custom, avoiding duplicates
  const categoryOptions = [
    ...STATIC_CATEGORIES,
    ...customCategories.filter((c) => !STATIC_CATEGORIES.includes(c)),
  ];

  if (!chat)
    return (
      <div className="container mt-6 has-text-centered">
        <div className="neumorphic-inset p-6 is-inline-block">
          <div
            className="loader is-loading"
            style={{ width: 50, height: 50 }}
          ></div>
        </div>
      </div>
    );
  console.log("Chat data", chat);
  return (
    <div
      className="container mt-4 mb-6"
      style={{
        paddingBottom: isMobile ? "150px" : 0, // Add padding to prevent content from being hidden
      }}
    >
      {/* Link to custom messages manager */}

      <div className="columns is-centered">
        <div
          className="column is-8-desktop is-10-tablet is-12-mobile"
          style={{ padding: 0 }}
        >
          <div
            className="neumorphic p-0"
            style={{
              overflow: "hidden",
              position: "relative",
              "@media (max-width: 768px)": {
                minHeight: "100vh",
                borderRadius: 0,
              },
            }}
          >
            {/* Chat Header */}
            <div className="p-4" style={{ background: "var(--bg-secondary)" }}>
              {isMobile ? (
                // Mobile Header
                <>
                  <div className="is-flex is-justify-content-space-between align-items-center">
                    <div className="is-flex align-items-center">
                      <Link to={`/user/${chat.user._id}`}>
                        <div
                          className="neumorphic-inset mr-3 is-flex is-justify-content-center is-align-items-center"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            color: "var(--accent-primary)",
                            fontSize: "1.1em",
                            fontWeight: "600",
                          }}
                        >
                          {chat.user.name[0].toUpperCase()}
                        </div>
                      </Link>
                      <div>
                        <Link to={`/user/${chat.user._id}`}>
                          <p
                            className="title is-5 mb-1"
                            style={{
                              color: chat.is_premium
                                ? "var(--primary)"
                                : "var(--text-primary)",
                            }}
                          >
                            {chat.user.name}
                            {chat.push_token && (
                              <>
                                <span className="tag is-small">PUSH</span>
                              </>
                            )}
                          </p>
                        </Link>
                        <div className="is-flex align-items-center">
                          <span className="tag is-small neumorphic mr-2">
                            {chat.type === "feedback" ? "Feedback" : "Review"}
                          </span>
                          {chat.type === "course_review" && (
                            <span
                              className="tag is-small neumorphic"
                              style={{
                                color:
                                  chat.rating >= 4
                                    ? "#00d1b2"
                                    : chat.rating >= 3
                                    ? "#ffdd57"
                                    : "#ff3860",
                              }}
                            >
                              {chat.rating}/5
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      className="button is-small is-neumorphic"
                      onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
                    >
                      <span className="icon">
                        <FontAwesomeIcon
                          icon={isHeaderExpanded ? faChevronUp : faChevronDown}
                        />
                      </span>
                    </button>
                  </div>

                  {/* Expandable Header Content for Mobile */}
                  {isHeaderExpanded && (
                    <div className="mt-4 mobile-header-details">
                      <div className="is-flex is-justify-content-between mb-3">
                        {/* <p style={{ color: 'var(--text-secondary)' }}>{chat.user.email}</p> */}
                        <p
                          style={{ color: "var(--text-secondary)" }}
                          className="is-size-7"
                        >
                          {new Date(chat.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </p>
                      </div>
                      {chat.course && (
                        <Link
                          to={`/course/${chat.course._id}`}
                          className="is-block mb-3"
                        >
                          <div
                            className="is-flex align-items-center"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            <FontAwesomeIcon icon={faBook} className="mr-2" />
                            <span>{chat.course.title}</span>
                          </div>
                        </Link>
                      )}
                      <div
                        className="is-flex is-flex-wrap-wrap"
                        style={{
                          flexWrap: "wrap",
                          gap: "0.5rem",
                          overflowX: "auto",
                        }}
                      >
                        <div className="select is-small is-neumorphic mr-2 mb-2">
                          <select
                            value={chat.status || "Unhandled"}
                            onChange={(e) => handleStatusChange(e.target.value)}
                          >
                            <option>Unhandled</option>
                            <option>Handled</option>
                            <option>Waiting</option>
                            <option>Closed</option>
                          </select>
                        </div>
                        <button
                          onClick={handleReload}
                          className="button is-small is-neumorphic mb-2"
                          aria-label="Reload chat"
                        >
                          <span className="icon">
                            <FontAwesomeIcon icon={faRotate} />
                          </span>
                        </button>
                        {/* folder selector */}
                        <div className="select is-small is-neumorphic mr-2 mb-2">
                          <select
                            value={chat.folder}
                            onChange={async (e) => {
                              const f = e.target.value;
                              await updateChatFolder(
                                chat.type + "s",
                                chat._id,
                                f
                              );
                              setChat((prev) => ({ ...prev, folder: f }));
                            }}
                          >
                            {folders.map((f) => (
                              <option key={f} value={f}>
                                {f}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Desktop Header
                <div className="is-flex is-justify-content-space-between align-items-center">
                  <div className="is-flex align-items-center">
                    <Link to={`/user/${chat.user._id}`}>
                      {" "}
                      <div
                        className="neumorphic-inset mr-3 is-flex is-justify-content-center is-align-items-center"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          color: "var(--accent-primary)",
                          fontSize: "1.2em",
                          fontWeight: "600",
                        }}
                      >
                        {chat.user.name[0].toUpperCase()}
                      </div>
                    </Link>
                    <div>
                      <Link to={`/user/${chat.user._id}`}>
                        <p
                          className="title is-4 mb-1"
                          style={{
                            color: chat.is_premium
                              ? "var(--accent-primary)"
                              : "var(--text-primary)",
                          }}
                        >
                          {chat.user.name}
                          {chat.push_token && (
                            <>
                              <span className="tag is-small ml-2">PUSH</span>
                            </>
                          )}
                        </p>
                        <p
                          className="subtitle is-6 mb-0"
                          style={{ color: "var(--text-secondary)" }}
                        >
                          {chat.user.email}
                        </p>
                      </Link>
                      {chat.course && (
                        <div className="mt-5">
                          <Link
                            to={`/course/${chat.course._id}`}
                            className="title is-4 "
                            style={{ color: "var(--text-secondary)" }}
                          >
                            <FontAwesomeIcon icon={faBook} className="mr-2" />
                            {chat.course.title}
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="is-flex is-flex-direction-column is-align-items-flex-end">
                    <div className="tags mb-2">
                      <span
                        className="tag is-medium neumorphic"
                        style={{ color: "var(--accent-primary)" }}
                      >
                        {chat.type === "feedback"
                          ? "Feedback"
                          : "Course Review"}
                      </span>
                      {chat.type === "course_review" && (
                        <span
                          className="tag is-medium neumorphic"
                          style={{
                            color:
                              chat.rating >= 4
                                ? "#00d1b2"
                                : chat.rating >= 3
                                ? "#ffdd57"
                                : "#ff3860",
                          }}
                        >
                          {chat.rating}/5
                        </span>
                      )}
                    </div>

                    <div className="is-flex">
                      <div className="select is-neumorphic mr-2">
                        <select
                          value={chat.status}
                          onChange={(e) => handleStatusChange(e.target.value)}
                        >
                          <option>Unhandled</option>
                          <option>Handled</option>
                          <option>Waiting</option>
                          <option>Closed</option>
                        </select>
                      </div>
                      <button
                        onClick={handleReload}
                        className="button is-neumorphic"
                        aria-label="Reload chat"
                      >
                        <span className="icon">
                          <FontAwesomeIcon icon={faRotate} />
                        </span>
                      </button>
                      {/* folder selector */}
                      <div className="select is-neumorphic ml-2">
                        <select
                          value={chat.folder}
                          onChange={async (e) => {
                            const f = e.target.value;
                            await updateChatFolder(
                              chat.type + "s",
                              chat._id,
                              f
                            );
                            setChat((prev) => ({ ...prev, folder: f }));
                          }}
                        >
                          {folders.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Content */}
            <div
              className="p-4"
              style={{
                height: isMobile
                  ? `calc(100vh - ${keyboardVisible ? "380px" : "280px"} - ${
                      isHeaderExpanded ? "200px" : "80px"
                    })`
                  : "calc(100vh - 450px)",
                transition: "height 0.3s ease",
                paddingBottom: isMobile ? "80px" : "1rem", // Add padding to prevent content from being hidden
              }}
            >
              <div
                className="messages-container neumorphic-inset"
                style={{
                  height: "calc(100%)",
                  overflowY: "auto",
                  padding: "1rem",
                  borderRadius: "15px",
                }}
              >
                {/* Original Message */}
                <div className="original-message mb-4">
                  <div className="neumorphic p-4" style={{ maxWidth: "85%" }}>
                    <p style={{ color: "var(--text-primary)" }}>
                      {chat.content}
                    </p>
                  </div>
                </div>

                {/* Chat Messages */}
                {chat.messages?.map((message, index) => (
                  <div
                    key={index}
                    className={`message mb-4`}
                    style={{
                      display: "flex",
                      justifyContent:
                        message.sender === "Support"
                          ? "flex-end"
                          : "flex-start",
                    }}
                  >
                    <div
                      className={`neumorphic p-3`}
                      style={{
                        maxWidth: "75%",
                        background:
                          message.sender === "Support"
                            ? "var(--accent-primary)"
                            : "var(--bg-secondary)",
                        color:
                          message.sender === "Support"
                            ? "#fff"
                            : "var(--text-primary)",
                        textAlign: "left",
                      }}
                    >
                      <p style={{ whiteSpace: "pre-wrap" }}>
                        {message.content}
                      </p>
                      <p className="is-size-7 mt-1" style={{ opacity: 0.8 }}>
                        {message.sender === "Support" ? "You" : chat.user.name}{" "}
                        •{" "}
                        {new Date(
                          message.timestamp || chat.updated_at
                        ).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Move chat input outside the neumorphic container */}
      {isMobile && (
        <div
          className="chat-input-fixed"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: "var(--bg-secondary)",
            padding: "1rem",
            zIndex: 1000,
            borderTop: "1px solid var(--border-color)",
            width: "100%",
            borderRadius: "15px",
          }}
        >
          <div className="field" style={{ maxWidth: "100%" }}>
            <div className="control">
              <TextareaAutosize
                className="textarea neumorphic-inset"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your message..."
                minRows={2}
                maxRows={4}
                style={{
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  fontSize: "16px",
                  resize: "none",
                  overflow: "hidden",
                }}
              />
            </div>
            <div className="is-flex is-justify-content-space-between mt-3">
              <button
                className="button is-neumorphic is-small-mobile"
                onClick={openBuilder}
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faFeather} />
                </span>
              </button>
              <button
                className="button is-neumorphic is-small-mobile"
                style={{
                  background: reply.trim()
                    ? "var(--accent-primary)"
                    : "var(--bg-primary)",
                  color: reply.trim() ? "#fff" : "var(--text-secondary)",
                }}
                onClick={sendMessage}
                disabled={!reply.trim()}
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faPaperPlane} />
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop input remains inside the neumorphic container */}
      {!isMobile && (
        <div
          className="p-4"
          style={{
            background: "var(--bg-secondary)",
            borderTop: "1px solid var(--border-color)",
            borderRadius: "15px",
          }}
        >
          <div className="field" style={{ maxWidth: "100%" }}>
            <div className="control">
              <TextareaAutosize
                className="textarea neumorphic-inset"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your message..."
                minRows={3}
                maxRows={6}
                style={{
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  fontSize: "inherit",
                  resize: "none",
                }}
              />
            </div>
            <div className="is-flex is-justify-content-space-between mt-3">
              <button
                className="button is-neumorphic is-small-mobile"
                onClick={openBuilder}
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faFeather} />
                </span>
                <span className={isMobile ? "d-none" : ""}>
                  Message Builder
                </span>
              </button>
              <button
                className="button is-neumorphic is-small-mobile"
                style={{
                  background: reply.trim()
                    ? "var(--accent-primary)"
                    : "var(--bg-primary)",
                  color: reply.trim() ? "#fff" : "var(--text-secondary)",
                }}
                onClick={sendMessage}
                disabled={!reply.trim()}
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faPaperPlane} />
                </span>
                <span className={isMobile ? "d-none" : ""}>Send</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* message builder modal */}
      <MessageBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onInsert={handleInsert}
        username={chat.user.name}
        context={chat.content}
      />
    </div>
  );
};

export default ChatView;
